"use client";
import {Children, isValidElement, JSX, ReactNode, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {KeenSliderInstance, useKeenSlider} from "keen-slider/react";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {ButtonWithImage} from "@/shared/ui/buttons";
import {assetPath} from "@/shared/utils/asset-path";

import "./style.scss";
import "keen-slider/keen-slider.min.css";

interface CardSliderProps {
    slidesNumber: {
        desktop: number | "auto";
        mobile: number | "auto";
    };
    mobileDisabled?: boolean;
    children: ReactNode;
    space: SpaceProps;
    classname: string;
}
export function CardSlider({slidesNumber, mobileDisabled = false, children, space, classname}: CardSliderProps): JSX.Element {
    const sectionVars = useSectionSpace(space);

    const childrenArray = Children.toArray(children).filter(isValidElement);
    const childrenCount = childrenArray.length;
    const shouldUseSlider = childrenCount > 1;

    const [sliderInstance, setSliderInstance] = useState<KeenSliderInstance | null>(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    const [sliderRef] = useKeenSlider(
        shouldUseSlider
            ? {
                  slides: {
                      perView: slidesNumber.mobile,
                      spacing: 15,
                  },
                  breakpoints: {
                      "(min-width: 768px)": {
                          slides: {
                              perView: slidesNumber.desktop,
                              spacing: 20,
                          },
                      },
                      "(max-width: 767px)": {
                          disabled: mobileDisabled,
                      },
                  },
                  drag: true,
                  loop: true,
                  mode: "snap",
                  rtl: false,
              }
            : undefined,
        [
            (slider) => {
                setSliderInstance(slider);
                slider.on("slideChanged", () => {
                    setCurrentSlide(slider.track.details.rel);
                });
            },
        ],
    );

    const slideWidth =
        typeof slidesNumber.desktop === "number" ? `calc((100% - ${(slidesNumber.desktop - 1) * 15}px)/${slidesNumber.desktop})` : "auto";
    const mobileDisabledClass = mobileDisabled ? "mob-disabled" : "mob-active";

    return (
        <div className={`card-slider ${classname}`} style={sectionVars}>
            {shouldUseSlider ? (
                <>
                    <div ref={sliderRef} className="keen-slider card-slider__slider">
                        {childrenArray.map((child, index) => {
                            const key = isValidElement(child) && child.key ? child.key : index;
                            return (
                                <div
                                    key={key}
                                    className={`keen-slider__slide card-slider__slide ${mobileDisabledClass}`}
                                    style={{width: `${slideWidth}`}}
                                >
                                    {child}
                                </div>
                            );
                        })}
                    </div>
                    <div className="card-slider__navigation-wrap">
                        <div className="card-slider__navigation">
                            <ButtonWithImage
                                variant="primary"
                                onClick={() => sliderInstance?.prev()}
                                aria-label="Previous slide"
                                className="card-slider__nav-button card-slider__nav-button--prev"
                            >
                                <SmartImage
                                    src={assetPath("/images/icons/navigation/arrow-left.svg")}
                                    alt="arrow"
                                    width={16}
                                    height={16}
                                    aria-hidden="true"
                                />
                            </ButtonWithImage>
                            <ButtonWithImage
                                variant="primary"
                                onClick={() => sliderInstance?.next()}
                                aria-label="Next slide"
                                className="card-slider__nav-button card-slider__nav-button--next"
                            >
                                <SmartImage
                                    src={assetPath("/images/icons/navigation/arrow-right.svg")}
                                    alt="arrow"
                                    width={16}
                                    height={16}
                                    aria-hidden="true"
                                />
                            </ButtonWithImage>
                        </div>
                    </div>

                    {childrenArray.length > 1 && (
                        <div className="card-slider__dots">
                            {childrenArray.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`card-slider__dot ${currentSlide === index ? "card-slider__dot--active" : ""}`}
                                    aria-label={`Go to slide ${index + 1}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        sliderInstance?.moveToIdx(index);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div className="card-slider__content-items">{children}</div>
            )}
        </div>
    );
}
